import type { LngLat, Map } from 'mapbox-gl';
import { useEffect, useRef } from 'react';
import {
  updateBoundingBox,
  updateExploreActionType,
  updateLocationResult,
  updateMapRotation,
  updateNaturalLanguageSearchIsActive,
  updateSkipQuery
} from '@alltrails/explore-service/redux/exploreServiceSlice';
import type ExploreEventData from '@alltrails/explore-service/types/ExploreEventData';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import getMapRotation from '@alltrails/explore-service/utils/getMapRotation';
import useMap from '@alltrails/maps/hooks/useMap';
import { useAppDispatch } from '@/utils/redux';
import { setReturningBounds } from '@/utils/slices/exploreSlice';

type MoveEvent = { target: Map } & Partial<ExploreEventData>;

const useExploreServiceMapMove = (debugEnabled?: boolean, preserveLocationResultOnMove?: boolean, preventQueryOnMove?: boolean) => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const mapIsInitialized = useRef(false);
  const previousCenter = useRef<LngLat | undefined>(undefined);

  useEffect(() => {
    const onFirstIdle = () => {
      mapIsInitialized.current = true;

      if (!preventQueryOnMove) {
        dispatch(updateSkipQuery(false));
      }
    };

    map?.once('idle', onFirstIdle);
    return () => {
      map?.off('idle', onFirstIdle);
    };
  }, [dispatch, map, preventQueryOnMove]);

  useEffect(() => {
    const onMoveStart = (event: MoveEvent) => {
      previousCenter.current = event.target.getCenter();
    };

    const onMoveEnd = (event: MoveEvent) => {
      const boundingBox = getMapBoundingBox(event.target);
      const mapRotation = getMapRotation(event.target);
      dispatch(updateBoundingBox(event.boundingBox ?? boundingBox));
      dispatch(updateMapRotation(event.mapRotation ?? mapRotation));

      const mapBounds = event.target.getBounds();
      if (mapBounds) {
        dispatch(
          setReturningBounds({
            longitudeBottomRight: mapBounds._ne.lng,
            latitudeBottomRight: mapBounds._sw.lat,
            longitudeTopLeft: mapBounds._sw.lng,
            latitudeTopLeft: mapBounds._ne.lat
          })
        );
      }

      if (!mapIsInitialized.current) {
        return;
      }

      if (!preventQueryOnMove) {
        dispatch(updateSkipQuery(false));
      }

      if (!event.preserveLocationResult && !event.naturalLanguageSearchTriggered) {
        // Don't update the ExploreActionType on the initial moveEnd since that is just the initial load
        dispatch(updateExploreActionType('map interaction'));
      }

      // Mapbox centers can change ever so slightly when zooming and such, so use a 1 meter margin of error to be safe
      const centerHasChanged = previousCenter.current ? previousCenter.current.distanceTo(event.target.getCenter()) > 1 : false;
      if (!(event.preserveLocationResult || preserveLocationResultOnMove) && centerHasChanged) {
        dispatch(updateLocationResult(undefined));
      }

      if (!event.naturalLanguageSearchTriggered) {
        // When the map is manually moved (or moved by something other than a natural language search), consider the natural language search inactive
        dispatch(updateNaturalLanguageSearchIsActive(false));
      }

      if (debugEnabled) {
        console.log('map zoom: ', map?.getZoom());
      }
    };

    map?.on('movestart', onMoveStart);
    map?.on('moveend', onMoveEnd);
    return () => {
      map?.off('movestart', onMoveStart);
      map?.off('moveend', onMoveEnd);
    };
  }, [debugEnabled, dispatch, map, preserveLocationResultOnMove, preventQueryOnMove]);
};

export default useExploreServiceMapMove;
